import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { useNavigate } from "react-router-dom";
import {
  Search,
  User,
  ShoppingCart,
  Tag,
  Clock,
  CreditCard,
  DollarSign,
  Plus,
  Minus,
  X,
  FileText,
  UserPlus,
  Printer,
  Barcode,
  Package2,
  Save,
  History,
  Layers,
  CircleDollarSign,
  Bell,
  BadgePercent,
  Ticket,
  Wallet,
  BadgeDollarSign,
} from "lucide-react";
import DashboardLayout from "@/components/dashboard/DashboardLayout";

// Sample product data
const products = [
  { id: 1, code: "P001", name: "Rice 5kg", price: 25.50, category: "Groceries", hasPromo: true, hasSubsidy: true },
  { id: 2, code: "P002", name: "Cooking Oil 1L", price: 9.90, category: "Groceries", hasPromo: false, hasSubsidy: true },
  { id: 3, code: "P003", name: "Chicken Eggs (Grade A)", price: 12.80, category: "Fresh", hasPromo: true, hasSubsidy: false },
  { id: 4, code: "P004", name: "Milo 1kg", price: 23.50, category: "Beverages", hasPromo: false, hasSubsidy: false },
  { id: 5, code: "P005", name: "Bread", price: 3.90, category: "Bakery", hasPromo: true, hasSubsidy: false },
];

// Sample categories
const categories = [
  { id: "all", name: "All", icon: <ShoppingCart className="h-4 w-4" /> },
  { id: "favorites", name: "Favorites", icon: <Tag className="h-4 w-4" /> },
  { id: "groceries", name: "Groceries", icon: <ShoppingCart className="h-4 w-4" /> },
  { id: "fresh", name: "Fresh", icon: <ShoppingCart className="h-4 w-4" /> },
  { id: "beverages", name: "Beverages", icon: <ShoppingCart className="h-4 w-4" /> },
  { id: "bakery", name: "Bakery", icon: <ShoppingCart className="h-4 w-4" /> },
];

// Payment types
const paymentTypes = [
  { id: "cash", name: "Cash" },
  { id: "credit", name: "Credit Card" },
  { id: "ewallet", name: "E-wallet" },
  { id: "duitnow", name: "DuitNow QR" },
  { id: "voucher", name: "Voucher" },
  { id: "subsidy", name: "Subsidy" },
  { id: "credit", name: "Store Credit" },
];

// Order types
const orderTypes = [
  { id: "walkin", name: "Walk-In" },
  { id: "online", name: "JimatShop Online" },
  { id: "preorder", name: "JimatShop Preorder" },
];

// Sample promotions
const promotions = [
  { id: 1, code: "PROMO10", description: "10% off all items", discount: 10, type: "percent", validUntil: "2025-06-12" },
  { id: 2, code: "BUNDLE5", description: "Buy 2 Get 1 Free on selected items", validUntil: "2025-05-30" },
  { id: 3, code: "RAMADAN25", description: "RM25 off orders above RM200", discount: 25, type: "fixed", validUntil: "2025-05-15" },
];

// Sample vouchers
const vouchers = [
  { id: 1, code: "JIMAT50", description: "RM50 off first purchase", amount: 50, validUntil: "2025-06-30" },
  { id: 2, code: "LOYAL20", description: "RM20 birthday voucher", amount: 20, validUntil: "2025-07-15" },
];

// Sample online orders notifications
const onlineOrderNotifications = [
  { id: 1, type: "online", customerName: "Ahmad Bin Ali", orderTime: "2025-05-12 09:15", items: 5, total: 126.80, status: "New", read: false },
  { id: 2, type: "preorder", customerName: "Sarah Johnson", orderTime: "2025-05-12 10:30", items: 8, total: 245.60, status: "New", read: false },
  { id: 3, type: "online", customerName: "Raj Kumar", orderTime: "2025-05-11 16:45", items: 3, total: 85.20, status: "Completed", read: true },
];

const POSv2 = () => {
  const [barcode, setBarcode] = useState("");
  const [activeCategory, setActiveCategory] = useState("all");
  const [paymentType, setPaymentType] = useState("cash");
  const [orderType, setOrderType] = useState("walkin");
  const [currentTime, setCurrentTime] = useState(new Date());
  const [cartItems, setCartItems] = useState([
    { id: 1, code: "P001", name: "Rice 5kg", price: 25.50, quantity: 2, discount: 2.50, tax: 1.38, hasPromo: true, hasSubsidy: true },
    { id: 3, code: "P003", name: "Chicken Eggs (Grade A)", price: 12.80, quantity: 1, discount: 0, tax: 0.77, hasPromo: true, hasSubsidy: false },
  ]);
  const [showNotifications, setShowNotifications] = useState(false);
  const [notifications, setNotifications] = useState(onlineOrderNotifications);
  const [activePromotion, setActivePromotion] = useState<number | null>(null);
  const [activeVoucher, setActiveVoucher] = useState<number | null>(null);
  const { toast } = useToast();
  const navigate = useNavigate();
  
  // Count unread notifications
  const unreadCount = notifications.filter(notification => !notification.read).length;
  
  // Update time every second
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    
    return () => {
      clearInterval(timer);
    };
  }, []);
  
  // Calculate order summary
  const subtotal = cartItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  const totalDiscount = cartItems.reduce((sum, item) => sum + item.discount, 0);
  const totalTax = cartItems.reduce((sum, item) => sum + item.tax, 0);
  const serviceFee = 5.00;
  const rounding = 0.40;
  const grandTotal = subtotal - totalDiscount + serviceFee + totalTax + rounding;
  const amountDue = grandTotal;
  const received = 200;
  const changeDue = received - amountDue;
  
  // Store credit and subsidy information
  const storeCredit = 150.00;
  const subsidyBalance = 75.00;

  // Handle barcode input
  const handleBarcodeSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    console.log("Scanning barcode:", barcode);
    
    if (barcode.trim()) {
      // In a real app, you would look up the product by barcode
      toast({
        title: "Scanning barcode",
        description: `Searching for product with barcode: ${barcode}`,
      });
    } else {
      toast({
        variant: "destructive",
        title: "Empty barcode",
        description: "Please enter a barcode to scan",
      });
    }
    
    setBarcode("");
  };

  // Handle quantity change
  const handleQuantityChange = (id: number, change: number) => {
    setCartItems(cartItems.map(item => 
      item.id === id ? { ...item, quantity: Math.max(1, item.quantity + change) } : item
    ));
  };

  // Handle remove item
  const handleRemoveItem = (id: number) => {
    setCartItems(cartItems.filter(item => item.id !== id));
  };

  // Handle notification click
  const handleNotificationClick = (id: number) => {
    // Mark notification as read
    setNotifications(notifications.map(notification =>
      notification.id === id ? { ...notification, read: true } : notification
    ));
    
    // Show order details (in a real app, this would open the order details)
    const notification = notifications.find(n => n.id === id);
    toast({
      title: "Order from " + notification?.customerName,
      description: `${notification?.items} items, Total: RM ${notification?.total.toFixed(2)}`,
      duration: 5000,
    });
    
    // Close notifications panel
    setShowNotifications(false);
  };
  
  // Apply promotion
  const handleApplyPromotion = (id: number) => {
    setActivePromotion(id === activePromotion ? null : id);
    toast({
      title: "Promotion Applied",
      description: `Promotion code ${promotions.find(p => p.id === id)?.code} has been applied`,
      duration: 3000,
    });
  };
  
  // Apply voucher
  const handleApplyVoucher = (id: number) => {
    setActiveVoucher(id === activeVoucher ? null : id);
    toast({
      title: "Voucher Applied",
      description: `Voucher ${vouchers.find(v => v.id === id)?.code} has been applied`,
      duration: 3000,
    });
  };

  // Navigate to customer search
  const handleFindCustomer = () => {
    navigate("/dashboard/customer-search");
  };

  // Navigate to product search
  const handleFindProduct = () => {
    navigate("/dashboard/product-search");
  };

  // Navigate to stock levels
  const handleViewStock = () => {
    navigate("/dashboard/advanced-inventory", { state: { defaultTab: "stock-levels" } });
  };

  // View all orders
  const handleViewAllOrders = () => {
    navigate("/dashboard/sales-jimatshop");
  };

  return (
    <div className="space-y-4">
      {/* Header Section */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-2">
        <div className="col-span-2">
          <h1 className="text-2xl font-bold text-[#333333] mb-1">JimatPOS Sales</h1>
          <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
            <div className="flex items-center">
              <span className="font-medium mr-2">Outlet:</span> Jimat Mart Ampang
            </div>
            <div className="flex items-center">
              <Clock className="h-4 w-4 mr-1" />
              <span>{currentTime.toLocaleString()}</span>
            </div>
            <div className="flex items-center">
              <User className="h-4 w-4 mr-1" />
              <span>Staff: Amirul (Cashier)</span>
            </div>
            <div className="flex items-center">
              <Tag className="h-4 w-4 mr-1" />
              <span>Shift: Morning (08:00 - 16:00)</span>
            </div>
          </div>
        </div>
        <div className="hidden md:flex md:justify-end">
          <div className="flex space-x-2">
            <div className="relative">
              <Button 
                variant="outline" 
                size="sm" 
                className="h-9 relative" 
                onClick={() => setShowNotifications(!showNotifications)}
              >
                <Bell className="h-4 w-4 mr-1" />
                Orders
                {unreadCount > 0 && (
                  <span className="absolute top-0 right-0 h-4 w-4 rounded-full bg-red-500 text-[10px] text-white flex items-center justify-center transform translate-x-1 -translate-y-1">
                    {unreadCount}
                  </span>
                )}
              </Button>
              {showNotifications && (
                <div className="absolute top-full right-0 mt-1 w-80 bg-white border border-gray-200 rounded-md shadow-lg z-50">
                  <div className="p-2 border-b border-gray-200 font-medium">
                    Order Notifications
                  </div>
                  <div className="max-h-96 overflow-y-auto">
                    {notifications.length === 0 ? (
                      <div className="p-3 text-center text-sm text-gray-500">
                        No new orders
                      </div>
                    ) : (
                      notifications.map((notification) => (
                        <div 
                          key={notification.id} 
                          className={`p-3 border-b border-gray-100 cursor-pointer hover:bg-gray-50 ${notification.read ? '' : 'bg-blue-50'}`}
                          onClick={() => handleNotificationClick(notification.id)}
                        >
                          <div className="flex justify-between">
                            <span className="font-medium">{notification.customerName}</span>
                            <Badge variant={notification.read ? "outline" : "default"}>
                              {notification.type === "online" ? "Online" : "Preorder"}
                            </Badge>
                          </div>
                          <div className="text-sm text-gray-500 mt-1">
                            {notification.items} items · RM {notification.total.toFixed(2)}
                          </div>
                          <div className="text-xs text-gray-400 mt-1 flex items-center">
                            <Clock className="h-3 w-3 mr-1" />
                            {notification.orderTime}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                  {notifications.length > 0 && (
                    <div className="p-2 text-center border-t border-gray-200">
                      <Button variant="ghost" size="sm" className="text-sm text-blue-600">
                        View All Orders
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </div>
            <Button variant="outline" size="sm" className="h-9">
              <History className="h-4 w-4 mr-1" />
              Bill History
            </Button>
            <Button variant="outline" size="sm" className="h-9">
              EN / BM
            </Button>
          </div>
        </div>
      </div>

      {/* Top Bar Controls */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="shadow-sm col-span-2 border-[#8CC84B]/20">
          <CardContent className="flex flex-wrap gap-2 py-3">
            <form onSubmit={handleBarcodeSubmit} className="flex items-center space-x-2 w-full md:w-auto flex-1 min-w-[200px]">
              <div className="relative flex-1">
                <Barcode className="h-4 w-4 absolute left-3 top-3 text-muted-foreground" />
                <Input
                  type="text"
                  placeholder="Scan barcode"
                  value={barcode}
                  onChange={(e) => setBarcode(e.target.value)}
                  autoFocus
                  className="pl-10"
                />
              </div>
              <Button type="submit" size="sm" variant="outline" className="whitespace-nowrap h-10">
                <Search className="h-4 w-4 mr-1" />
                Find Item
              </Button>
            </form>

            <div className="flex space-x-2 flex-wrap gap-2">
              <select 
                className="h-10 rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={orderType}
                onChange={(e) => setOrderType(e.target.value)}
              >
                {orderTypes.map(type => (
                  <option key={type.id} value={type.id}>{type.name}</option>
                ))}
              </select>

              <select 
                className="h-10 rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={paymentType}
                onChange={(e) => setPaymentType(e.target.value)}
              >
                {paymentTypes.map(type => (
                  <option key={type.id} value={type.id}>{type.name}</option>
                ))}
              </select>

              <Button variant="outline" size="sm" className="h-10" onClick={handleFindCustomer}>
                <User className="h-4 w-4 mr-1" />
                Find Customer
              </Button>
              
              <Button variant="outline" size="sm" className="h-10" onClick={handleFindProduct}>
                <Search className="h-4 w-4 mr-1" />
                Find Product
              </Button>
              
              <Button variant="outline" size="sm" className="h-10" onClick={handleViewStock}>
                <Package2 className="h-4 w-4 mr-1" />
                View Stock
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card className="shadow-sm border-[#8CC84B]/20">
          <CardContent className="py-3 space-y-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-1">
                <User className="h-4 w-4 text-[#4D9E41]" />
                <span className="text-sm font-medium">Walk-in Customer</span>
                <Badge variant="outline" className="text-blue-800 bg-blue-100 hover:bg-blue-200 border-blue-200">
                  Regular
                </Badge>
              </div>
              <Button variant="ghost" size="sm" className="h-7 px-3 text-xs" onClick={handleFindCustomer}>
                <UserPlus className="h-3 w-3 mr-1" />
                Change
              </Button>
            </div>
            <div className="flex items-center space-x-1">
              <CircleDollarSign className="h-4 w-4 text-[#4D9E41]" />
              <span className="text-sm">Loyalty: 250 pts</span>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content - 70/30 Split */}
      <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
        {/* Left Panel - 70% */}
        <div className="md:col-span-8 space-y-4">
          {/* Categories */}
          <Card className="shadow-sm border-[#8CC84B]/20">
            <CardHeader className="py-3">
              <CardTitle className="text-sm">Categories</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {categories.map((category) => (
                  <Button 
                    key={category.id}
                    variant={activeCategory === category.id ? "default" : "outline"}
                    size="sm"
                    className={`flex items-center space-x-1 ${activeCategory === category.id ? 'bg-[#8CC84B]' : ''}`}
                    onClick={() => setActiveCategory(category.id)}
                  >
                    {category.icon}
                    <span>{category.name}</span>
                  </Button>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Scanned Item Table */}
          <Card className="shadow-sm border-[#8CC84B]/20">
            <CardHeader className="py-3">
              <CardTitle className="text-sm">Scanned Items</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="border rounded-md overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Code</th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Product</th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Quantity</th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Price</th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Discount</th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tax</th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Subtotal</th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {cartItems.map((item) => (
                      <tr key={item.id}>
                        <td className="px-3 py-3 whitespace-nowrap text-sm">
                          {item.code}
                          {item.hasPromo && <span className="inline-block ml-1 px-1.5 py-0.5 bg-orange-100 text-orange-800 text-xs rounded">Promo</span>}
                          {item.hasSubsidy && <span className="inline-block ml-1 px-1.5 py-0.5 bg-blue-100 text-blue-800 text-xs rounded">Subsidy</span>}
                        </td>
                        <td className="px-3 py-3 whitespace-nowrap text-sm">{item.name}</td>
                        <td className="px-3 py-3 whitespace-nowrap text-sm">
                          <div className="flex items-center space-x-1">
                            <Button 
                              variant="outline" 
                              size="sm" 
                              className="h-6 w-6 p-0" 
                              onClick={() => handleQuantityChange(item.id, -1)}
                            >
                              <Minus className="h-3 w-3" />
                            </Button>
                            <span className="w-8 text-center">{item.quantity}</span>
                            <Button 
                              variant="outline" 
                              size="sm" 
                              className="h-6 w-6 p-0" 
                              onClick={() => handleQuantityChange(item.id, 1)}
                            >
                              <Plus className="h-3 w-3" />
                            </Button>
                          </div>
                        </td>
                        <td className="px-3 py-3 whitespace-nowrap text-sm">RM {item.price.toFixed(2)}</td>
                        <td className="px-3 py-3 whitespace-nowrap text-sm">
                          {item.discount > 0 && `RM ${item.discount.toFixed(2)}`}
                        </td>
                        <td className="px-3 py-3 whitespace-nowrap text-sm">
                          RM {item.tax.toFixed(2)}
                        </td>
                        <td className="px-3 py-3 whitespace-nowrap text-sm font-medium">
                          RM {((item.price * item.quantity) - item.discount).toFixed(2)}
                        </td>
                        <td className="px-3 py-3 whitespace-nowrap text-sm">
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="h-6 w-6 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                            onClick={() => handleRemoveItem(item.id)}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Right Panel - 30% */}
        <div className="md:col-span-4 space-y-4">
          {/* Customer Finance Panel - NEW */}
          <Card className="shadow-sm border-[#8CC84B]/20">
            <CardHeader className="py-3">
              <CardTitle className="text-sm">Customer Finance</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="space-y-3">
                {/* Promotions */}
                <div className="rounded-md border p-3">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center space-x-1">
                      <BadgePercent className="h-4 w-4 text-orange-500" />
                      <span className="font-medium text-sm">Promotions</span>
                    </div>
                    <Badge variant="outline" className="bg-orange-50 text-orange-700 hover:bg-orange-100 border-orange-200">
                      {promotions.length} Available
                    </Badge>
                  </div>
                  <div className="space-y-1.5">
                    {promotions.map((promotion) => (
                      <div 
                        key={promotion.id} 
                        className={`flex items-center justify-between text-sm p-1.5 rounded-sm cursor-pointer ${activePromotion === promotion.id ? 'bg-orange-100' : 'hover:bg-gray-50'}`}
                        onClick={() => handleApplyPromotion(promotion.id)}
                      >
                        <div>
                          <div className="font-medium">{promotion.code}</div>
                          <div className="text-xs text-muted-foreground">{promotion.description}</div>
                        </div>
                        <Button variant="ghost" size="sm" className="h-6 px-2 text-xs">
                          {activePromotion === promotion.id ? 'Applied' : 'Apply'}
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
                
                {/* Vouchers */}
                <div className="rounded-md border p-3">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center space-x-1">
                      <Ticket className="h-4 w-4 text-purple-500" />
                      <span className="font-medium text-sm">Vouchers</span>
                    </div>
                    <Badge variant="outline" className="bg-purple-50 text-purple-700 hover:bg-purple-100 border-purple-200">
                      {vouchers.length} Available
                    </Badge>
                  </div>
                  <div className="space-y-1.5">
                    {vouchers.map((voucher) => (
                      <div 
                        key={voucher.id} 
                        className={`flex items-center justify-between text-sm p-1.5 rounded-sm cursor-pointer ${activeVoucher === voucher.id ? 'bg-purple-100' : 'hover:bg-gray-50'}`}
                        onClick={() => handleApplyVoucher(voucher.id)}
                      >
                        <div>
                          <div className="font-medium">{voucher.code}</div>
                          <div className="text-xs text-muted-foreground">{voucher.description}</div>
                        </div>
                        <Button variant="ghost" size="sm" className="h-6 px-2 text-xs">
                          {activeVoucher === voucher.id ? 'Applied' : 'Apply'}
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
                
                {/* Store Credit */}
                <div className="rounded-md border p-3">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center space-x-1">
                      <Wallet className="h-4 w-4 text-green-500" />
                      <span className="font-medium text-sm">Store Credit</span>
                    </div>
                    <Badge variant="outline" className="bg-green-50 text-green-700 hover:bg-green-100 border-green-200">
                      Available
                    </Badge>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span>Balance:</span>
                    <span className="font-medium">RM {storeCredit.toFixed(2)}</span>
                  </div>
                  <div className="mt-2">
                    <Button variant="outline" size="sm" className="w-full text-xs h-7">Use Store Credit</Button>
                  </div>
                </div>
                
                {/* Subsidy */}
                <div className="rounded-md border p-3">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center space-x-1">
                      <BadgeDollarSign className="h-4 w-4 text-blue-500" />
                      <span className="font-medium text-sm">Subsidies</span>
                    </div>
                    <Badge variant="outline" className="bg-blue-50 text-blue-700 hover:bg-blue-100 border-blue-200">
                      Eligible
                    </Badge>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span>Remaining:</span>
                    <span className="font-medium">RM {subsidyBalance.toFixed(2)}</span>
                  </div>
                  <div className="mt-2">
                    <Button variant="outline" size="sm" className="w-full text-xs h-7">Apply Subsidy</Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Summary & Payment Panel */}
          <Card className="shadow-sm border-[#8CC84B]/20">
            <CardHeader className="py-3">
              <CardTitle className="text-sm">Transaction Summary</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3 font-mono text-sm">
                <div className="grid grid-cols-2">
                  <Label className="text-muted-foreground">Total Amount:</Label>
                  <div className="text-right">RM {subtotal.toFixed(2)}</div>
                </div>
                
                <div className="grid grid-cols-2">
                  <Label className="text-muted-foreground">Discount:</Label>
                  <div className="text-right text-red-600">-RM {totalDiscount.toFixed(2)}</div>
                </div>
                
                <div className="grid grid-cols-2">
                  <Label className="text-muted-foreground">Service Fee:</Label>
                  <div className="text-right">+RM {serviceFee.toFixed(2)}</div>
                </div>
                
                <div className="grid grid-cols-2">
                  <Label className="text-muted-foreground">Tax:</Label>
                  <div className="text-right">+RM {totalTax.toFixed(2)}</div>
                </div>
                
                <div className="grid grid-cols-2">
                  <Label className="text-muted-foreground">Rounding:</Label>
                  <div className="text-right">RM {rounding.toFixed(2)}</div>
                </div>
                
                <Separator className="my-2" />
                
                <div className="grid grid-cols-2 font-bold">
                  <Label>Grand Total:</Label>
                  <div className="text-right">RM {grandTotal.toFixed(2)}</div>
                </div>
                
                <div className="grid grid-cols-2">
                  <Label className="text-muted-foreground">Received:</Label>
                  <div className="text-right">RM {received.toFixed(2)}</div>
                </div>
                
                <div className="grid grid-cols-2 font-bold">
                  <Label>Change Due:</Label>
                  <div className="text-right">RM {changeDue.toFixed(2)}</div>
                </div>
              </div>

              {/* Number Keypad */}
              <div className="mt-4 grid grid-cols-3 gap-2">
                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 0].map((num) => (
                  <Button 
                    key={num} 
                    variant="outline"
                    className="h-10 w-full"
                  >
                    {num}
                  </Button>
                ))}
                <Button variant="outline" className="h-10 w-full">00</Button>
                <Button variant="outline" className="h-10 w-full">.</Button>
              </div>

              {/* Action Buttons */}
              <div className="grid grid-cols-2 gap-2 mt-4">
                <Button className="bg-[#4D9E41]">
                  <CreditCard className="mr-2 h-4 w-4" />
                  Pay (F4)
                </Button>
                <Button variant="outline">
                  <X className="mr-2 h-4 w-4" />
                  Cancel
                </Button>
              </div>
            </CardContent>
          </Card>
          
          {/* Action Buttons */}
          <div className="grid grid-cols-2 gap-2">
            <Button variant="outline" size="sm">
              <Plus className="h-4 w-4 mr-1" />
              New Order
            </Button>
            <Button variant="outline" size="sm">
              <Clock className="h-4 w-4 mr-1" />
              Hold Bill
            </Button>
            <Button variant="outline" size="sm">
              <Layers className="h-4 w-4 mr-1" />
              Merge Bill
            </Button>
            <Button variant="outline" size="sm">
              <X className="h-4 w-4 mr-1" />
              Delete Order
            </Button>
            <Button variant="outline" size="sm">
              <Printer className="h-4 w-4 mr-1" />
              Print Receipt
            </Button>
            <Button variant="outline" size="sm">
              <Save className="h-4 w-4 mr-1" />
              Save Draft
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default POSv2;
